// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Vex/rhythmCheck.js
//
// An extension of VexFlow for uTheory.com to mark early/late/right/wrong
// for rhythm performances
// eslint-disable-next-line import/no-cycle
import type { StaveNote, SVGContext } from 'vexflow';
import { Glyph } from 'vexflow';
import type NoteHolder from '../uTheory.Notation/noteHolder';
import initGlyphs from './customGlyphs'; // the cycle is purely a type cycle

export interface RhythmCheckOptions {
  color?: string;
  strokeColor?: string;
  /** x pixels to offset */
  offset?: number;
  /** Vex counting -- defaults to 5, 1st ledger below clef */
  staffLine?: number;
  x?: number;
}

export type RhythmCheckStatusString =
  | 'missed'
  | 'veryEarly'
  | 'early'
  | 'green'
  | 'late'
  | 'veryLate';

const green = '#008000';
const greenStroke = '#005200';
const yellow = '#FF6E00';
const yellowStroke = '#623000';
const red = '#ff0000';
const redStroke = '#990000';

type RCGlyphInfo = {
  offset: number;
  glyphName: string;
  color: string;
  strokeColor: string;
  fontSize: number;
  recenter: boolean;
  isHalfNoteOrLonger: boolean;
};

class RhythmCheck {
  // Public constructor
  constructor(
    noteHolder: NoteHolder,
    label: RhythmCheckStatusString,
    options: RhythmCheckOptions = {},
  ) {
    this.noteHolder = noteHolder;
    this.options = options;
    // Adjust for the notehead positioning if needed:
    /*
    if(typeof this.options.offset !== "undefined" &&
      this.options.offset < 0) {
      console.log("spacing left");
      this.options.offset -= (noteHolder.staveNote.getNoteHeadBeginX() +
            noteHolder.staveNote.getNoteHeadEndX());
    }
*/

    const codeHead = noteHolder.staveNote!.glyph.code_head;
    const isHalfNoteOrLonger = !!noteHolder.rhythm?.fraction.greaterThanEquals(0.5);
    // console.log({ glyph: noteHolder.staveNote!.glyph });
    this.glyphData = {
      // "label" : [offset, glyphName, color, strokeColor, fontSize, recenter]
      check: {
        offset: 0,
        glyphName: 'checkMark',
        color: green,
        strokeColor: greenStroke,
        fontSize: 28,
        recenter: true,
        isHalfNoteOrLonger,
      },
      green: {
        offset: 0,
        glyphName: 'checkMark',
        color: green,
        strokeColor: greenStroke,
        fontSize: 28,
        recenter: true,
        isHalfNoteOrLonger,
      },
      early: {
        offset: -7,
        glyphName: codeHead,
        color: yellow,
        strokeColor: yellowStroke,
        fontSize: 35,
        recenter: false,
        isHalfNoteOrLonger,
      },
      late: {
        offset: 7,
        glyphName: codeHead,
        color: yellow,
        strokeColor: yellowStroke,
        fontSize: 35,
        recenter: false,
        isHalfNoteOrLonger,
      },
      veryEarly: {
        offset: -14.5,
        glyphName: codeHead,
        color: red,
        strokeColor: redStroke,
        fontSize: 35,
        recenter: false,
        isHalfNoteOrLonger,
      },
      veryLate: {
        offset: 14.5,
        glyphName: codeHead,
        color: red,
        strokeColor: redStroke,
        fontSize: 35,
        recenter: false,
        isHalfNoteOrLonger,
      },
      missed: {
        offset: 0,
        glyphName: 'xMark',
        color: red,
        strokeColor: redStroke,
        fontSize: 25,
        recenter: true,
        isHalfNoteOrLonger,
      },
      x: {
        offset: 0,
        glyphName: 'xMark',
        color: red,
        strokeColor: redStroke,
        fontSize: 25,
        recenter: true,
        isHalfNoteOrLonger,
      },
    };

    this.label = label;

    initGlyphs();
  }

  public noteHolder: NoteHolder;

  private options: RhythmCheckOptions;

  private glyphData: {
    check: RCGlyphInfo;
    green: RCGlyphInfo;
    early: RCGlyphInfo;
    late: RCGlyphInfo;
    veryEarly: RCGlyphInfo;
    veryLate: RCGlyphInfo;
    missed: RCGlyphInfo;
    x: RCGlyphInfo;
  };

  private label: RhythmCheckStatusString;

  private linePath: SVGGElement | false = false;

  private glyphPathGroup: SVGGElement | false = false;

  // private group?: SVGGElement;

  disconnectLine() {
    if (this.label === 'early') {
      this.label = 'veryEarly';
      this.options.color = 'red';
      this.delete();
      return true;
    }
    if (this.label === 'late') {
      this.label = 'veryLate';
      this.options.color = 'red';
      this.delete();
      return true;
    }
    return false;
  }

  delete() {
    if (this.linePath) {
      this.linePath.parentElement?.removeChild(this.linePath);
      this.linePath = false;
    }
    if (this.glyphPathGroup) {
      this.glyphPathGroup.parentElement?.removeChild(this.glyphPathGroup);
      this.glyphPathGroup = false;
    }

    const el = this.noteHolder.staveNote!.getAttribute('el');
    // console.log({ el });
    if (!el) return;
    const path = el.querySelector('.vf-notehead')?.firstChild;
    if (!path) return;
    path.style.stroke = 'black';
    path.style.fill = 'black';
  }

  changeNoteHeadColor(color: string) {
    (this.noteHolder.staveNote as StaveNote).setKeyStyle(0, {
      fillStyle: color,
      strokeStyle: color,
    });
  }

  draw() {
    this.delete();
    const noteHolder = this.noteHolder;
    const staveNote = noteHolder.staveNote as StaveNote;
    if (!staveNote) return;
    const ctx = staveNote.getContext() as SVGContext;

    const info = this.glyphData[this.label];
    const code = info.glyphName;
    const color = this.options.color || info.color;
    const strokeColor = this.options.strokeColor || info.strokeColor;
    const offset = this.options.offset ?? info.offset;
    const recenter = info.recenter;
    const point = info.fontSize; // font size.
    let staffLine = staveNote.getKeyLine(0);
    // Given as music theory lines -- convert to Vex Line here:
    staffLine = -staffLine + 5;
    const originalStaffLine = staffLine;
    staffLine = staveNote.getStemDirection() > 0 ? staffLine + 1 : staffLine - 1;
    staffLine = this.options.staffLine || staffLine;

    // // If the label's green, change the notehead color:
    if (this.label === 'green') {
      const path = staveNote.getAttribute('el').querySelector('.vf-notehead').firstChild;
      path.style.fill = green;
      return;
    }

    // Start in middle of new notehead:
    const halfWidth = (staveNote.getNoteHeadEndX() - staveNote.getNoteHeadBeginX()) / 2;

    let x = staveNote.getNoteHeadBeginX() + offset;
    x = recenter ? x + halfWidth : x;
    x = typeof this.options.x === 'undefined' ? x : this.options.x;

    const stave = staveNote.getStave();
    if (!stave) return; // This should never happen.

    const y = stave.getYForLine(staffLine);
    // console.log({ Glyph });
    const glyph = new Glyph(code, point);
    glyph.setStyle({
      strokeStyle: strokeColor,
      fillStyle: color,
    });
    this.glyphPathGroup = ctx.openGroup('rc-glyph');
    glyph.render(ctx, x, y);
    ctx.save();
    ctx.setStrokeStyle(strokeColor);
    ctx.setLineWidth(info.isHalfNoteOrLonger ? 0.5 : 1);
    ctx.stroke();
    ctx.restore();
    ctx.closeGroup();

    if (this.label === 'veryEarly' || this.label === 'veryLate' || this.label === 'missed') return;

    ctx.openGroup();
    // Draw a line between them:
    ctx.save();
    ctx.setLineDash([2, 2]);
    ctx.setStrokeStyle(color);
    ctx.setLineWidth(1);

    const x1 = x + halfWidth;
    const x2 = staveNote.getNoteHeadBeginX() + halfWidth;

    const y2 = stave.getYForLine(originalStaffLine);
    const y1 = stave.getYForLine(staffLine);

    this.linePath = ctx.openGroup('rc-line');
    ctx.beginPath().moveTo(x1, y1).lineTo(x2, y2).stroke();
    ctx.closeGroup();

    ctx.restore();
    ctx.closeGroup();
    // Cover up the line:
    this.changeNoteHeadColor('black');
  }
}

export default RhythmCheck;
